import React, { useState, memo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, Plus, Coffee, Zap, Target, Clock, Timer } from 'lucide-react';
import { POMODORO_PRESETS } from '../hooks/useFocusMode';
import { Z_CLASS } from '../../../constants/zIndex';
import { cn } from '../../../shared/utils/cn';
import { haptic } from '../../../shared/utils/haptics';

/**
 * FocusModeOverlay - Immersive focus mode interface
 */
export const FocusModeOverlay = memo(function FocusModeOverlay({
  task,
  isActive,
  timerMode,
  timeRemaining,
  totalTime,
  isPaused,
  formattedTime,
  progress,
  currentMessage,
  preset,
  onTogglePause,
  onStop,
  onExtend,
  onStartBreak,
  onComplete
}) {
  const [showExtendOptions, setShowExtendOptions] = useState(false);

  const handleTogglePause = useCallback(() => {
    haptic.tap();
    onTogglePause();
  }, [onTogglePause]);

  const handleStop = useCallback((completed) => {
    haptic.tap();
    onStop(completed);
  }, [onStop]);

  const handleExtend = useCallback((mins) => {
    haptic.tap();
    onExtend(mins);
    setShowExtendOptions(false);
  }, [onExtend]);

  const handleComplete = useCallback(() => {
    haptic.success();
    onComplete?.();
    onStop(true);
  }, [onComplete, onStop]);

  const handleStartBreak = useCallback(() => {
    haptic.tap();
    onStartBreak();
  }, [onStartBreak]);

  if (!isActive) return null;

  const getCategoryGradient = () => {
    const gradients = {
      vital: 'from-red-600 to-red-900',
      chore: 'from-amber-600 to-amber-900',
      pleasure: 'from-emerald-600 to-emerald-900',
      reward: 'from-purple-600 to-purple-900'
    };
    return gradients[task?.category] || 'from-zinc-700 to-zinc-900';
  };

  const breatheClass = timerMode === 'break' ? 'animate-pulse' : '';

  return (
    <motion.div
      className={cn('fixed inset-0 flex items-center justify-center', Z_CLASS.FOCUS_MODE_OVERLAY)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${getCategoryGradient()}`} />

      <div className="relative w-full max-w-md mx-auto px-6 py-8 text-center">
        {/* Close button */}
        <motion.button
          onClick={() => handleStop(false)}
          className="absolute top-4 right-4 p-2.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <X className="w-5 h-5 text-white" />
        </motion.button>

        {/* Mode indicator */}
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <span className={cn(
            'inline-block px-4 py-1.5 rounded-lg text-xs font-medium uppercase tracking-widest',
            timerMode === 'work' ? 'bg-white/20 text-white' : 'bg-white/10 text-white/80'
          )}>
            {timerMode === 'work' ? 'Focus' : 'Pause'}
          </span>
        </motion.div>

        {/* Task name */}
        <motion.h2
          className="text-2xl font-semibold text-white mb-2 px-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          {task?.task}
        </motion.h2>

        {/* Encouragement message */}
        <AnimatePresence mode="wait">
          {currentMessage && (
            <motion.p
              key={currentMessage}
              className="text-white/70 text-sm mb-8"
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
            >
              {currentMessage}
            </motion.p>
          )}
        </AnimatePresence>

        {/* Circular timer */}
        <motion.div
          className={cn('relative w-48 h-48 mx-auto mb-8', breatheClass)}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        >
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="5" />
            <motion.circle
              cx="50" cy="50" r="45" fill="none" stroke="white" strokeWidth="5" strokeLinecap="round"
              initial={{ strokeDasharray: '0 283' }}
              animate={{ strokeDasharray: `${progress * 2.83} 283` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-4xl font-bold text-white tracking-tight">{formattedTime}</span>
            {isPaused && (
              <motion.span
                className="text-[10px] font-medium text-white/60 uppercase tracking-widest mt-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                En pause
              </motion.span>
            )}
          </div>
        </motion.div>

        {/* Controls */}
        <motion.div
          className="flex items-center justify-center gap-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <motion.button
            onClick={handleTogglePause}
            className="w-16 h-16 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isPaused ? <Play className="w-7 h-7 ml-1" /> : <Pause className="w-7 h-7" />}
          </motion.button>

          {timerMode === 'work' && timeRemaining > 0 && (
            <motion.button
              onClick={() => setShowExtendOptions(!showExtendOptions)}
              className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Plus className="w-5 h-5" />
            </motion.button>
          )}
        </motion.div>

        {/* Extend options */}
        <AnimatePresence>
          {showExtendOptions && (
            <motion.div
              className="flex gap-2 justify-center mt-4"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {[5, 10, 15].map(mins => (
                <motion.button
                  key={mins}
                  onClick={() => handleExtend(mins)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium text-sm transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  +{mins} min
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Completion actions */}
        <AnimatePresence>
          {timeRemaining === 0 && (
            <motion.div
              className="mt-8 space-y-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <motion.button
                onClick={handleComplete}
                className="w-full py-3.5 bg-white text-zinc-900 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors hover:bg-zinc-100"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <Zap className="w-5 h-5" />
                Terminé ! Collecter XP
              </motion.button>

              {timerMode === 'work' && (
                <motion.button
                  onClick={handleStartBreak}
                  className="w-full py-3 bg-white/20 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors hover:bg-white/30"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <Coffee className="w-5 h-5" />
                  Prendre une pause
                </motion.button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Preset info */}
        <motion.div
          className="absolute bottom-6 left-0 right-0 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <span className="text-white/40 text-xs">{POMODORO_PRESETS[preset]?.label}</span>
        </motion.div>
      </div>
    </motion.div>
  );
});

/**
 * PomodoroPresetSelector - Duration selection
 */
export const PomodoroPresetSelector = memo(function PomodoroPresetSelector({ onSelect, currentPreset }) {
  const handleSelect = useCallback((key) => {
    haptic.tap();
    onSelect(key);
  }, [onSelect]);

  return (
    <div>
      <h4 className="text-xs font-medium text-zinc-400 mb-3 flex items-center gap-1.5">
        <Timer className="w-3.5 h-3.5 text-zinc-500" />
        Durée de focus
      </h4>
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(POMODORO_PRESETS).map(([key, preset]) => (
          <motion.button
            key={key}
            onClick={() => handleSelect(key)}
            className={cn(
              'p-3 rounded-lg border text-left transition-all',
              currentPreset === key
                ? 'bg-zinc-700 border-zinc-600 text-zinc-100'
                : 'glass-card text-zinc-400 hover:bg-zinc-800 hover:text-zinc-300'
            )}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="block text-sm font-medium">{preset.work} min</span>
            <span className="block text-[10px] text-zinc-500 mt-0.5">{preset.label.split('(')[0].trim()}</span>
          </motion.button>
        ))}
      </div>
    </div>
  );
});

/**
 * Just2MinButton - Quick 2-minute timer
 */
export const Just2MinButton = memo(function Just2MinButton({ onClick, isRunning, timeLeft }) {
  const handleClick = useCallback(() => {
    haptic.tap();
    onClick();
  }, [onClick]);

  if (isRunning) {
    return (
      <motion.div
        className="flex items-center gap-2 px-3 py-2 bg-zinc-700 rounded-lg text-zinc-200"
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
      >
        <Clock className="w-4 h-4 animate-pulse" />
        <span className="text-sm font-medium">
          {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
        </span>
        <span className="text-[9px] text-zinc-400">Tu peux continuer !</span>
      </motion.div>
    );
  }

  return (
    <motion.button
      onClick={handleClick}
      className="flex items-center gap-1.5 px-3 py-1.5 glass-card text-zinc-400 text-xs font-medium hover:bg-zinc-800 hover:text-zinc-300 transition-colors"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Clock className="w-3.5 h-3.5" />
      <span>Juste 2 min</span>
    </motion.button>
  );
});

/**
 * FocusButton - Trigger button
 */
export const FocusButton = memo(function FocusButton({ onClick, task }) {
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    haptic.tap();
    onClick(task);
  }, [onClick, task]);

  return (
    <motion.button
      onClick={handleClick}
      className="flex items-center gap-1.5 px-2.5 py-1.5 glass-card text-zinc-400 text-xs font-medium hover:bg-zinc-700 hover:text-zinc-200 transition-colors"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <Target className="w-3.5 h-3.5" />
      <span>Focus</span>
    </motion.button>
  );
});

/**
 * FocusStartModal - Modal for starting a focus session
 */
export const FocusStartModal = memo(function FocusStartModal({ task, onStart, onClose }) {
  const [selectedPreset, setSelectedPreset] = useState('standard');

  const handleStart = useCallback(() => {
    haptic.focusStart();
    onStart(task, selectedPreset);
  }, [task, selectedPreset, onStart]);

  const handleClose = useCallback(() => {
    haptic.tap();
    onClose();
  }, [onClose]);

  return (
    <motion.div
      className={cn('fixed inset-0 flex items-center justify-center p-4', 'glass-overlay', Z_CLASS.FOCUS_START_MODAL)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="glass-elevated w-full max-w-sm overflow-hidden"
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
      >
        {/* Header */}
        <div className="bg-[var(--bg-elevated,#111114)] p-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 glass-subtle rounded-lg flex items-center justify-center">
              <Target className="w-4 h-4 text-zinc-500" />
            </div>
            <div>
              <h3 className="font-medium text-sm text-zinc-100">Mode Focus</h3>
              <p className="text-zinc-500 text-xs truncate max-w-[200px]">{task.task}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 bg-[var(--bg-base,#09090b)]">
          <PomodoroPresetSelector onSelect={setSelectedPreset} currentPreset={selectedPreset} />

          <div className="mt-4 p-3 glass-subtle">
            <p className="text-xs text-zinc-500">
              <strong className="text-zinc-400">Astuce :</strong> Le mode focus t'aide à te concentrer en isolant visuellement la tâche.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 pt-0 flex gap-2 bg-[var(--bg-base,#09090b)]">
          <motion.button
            onClick={handleClose}
            className="flex-1 py-2.5 px-4 border border-white/[0.06] rounded-lg text-zinc-400 font-medium text-sm hover:bg-zinc-800 transition-colors"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            Annuler
          </motion.button>
          <motion.button
            onClick={handleStart}
            className="flex-1 py-2.5 px-4 bg-zinc-700 text-white rounded-lg font-medium text-sm hover:bg-zinc-600 flex items-center justify-center gap-2 transition-colors"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <Play className="w-4 h-4" />
            Commencer
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
});

export default FocusModeOverlay;
